import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import { browserHistory } from 'react-router';
import type { ChangeEvent, VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers/plural';
import { DEFAULT_VCS_APP, VCS_APP_DISPLAY } from '../../../../../shared/vcs/githubApp/apps';
import { useAddToast } from '../../../../contexts/ToastContext';
import { window } from '../../../../globals';
import { errorMessageFromResponse } from '../../../../helpers/errors';
import { useShouldShowQuickImportScanMethods } from '../../../../helpers/features/quickImportScanMethods';
import {
  useUpdateOrganizationQuickImportScanMethodsSettings,
} from '../../../../hooks/api/organization/useUpdateOrganizationQuickImportScanMethodsSettings';
import useTeams from '../../../../hooks/api/teams/useTeams';
import { useGitHubAppInstallation } from '../../../../hooks/api/vcs/githubApp/useGitHubAppInstallation';
import { useGitHubAppInstallations } from '../../../../hooks/api/vcs/githubApp/useGitHubAppInstallations';
import { useGitHubAppRepositories } from '../../../../hooks/api/vcs/githubApp/useGitHubAppRepositories';
import { useImportGitHubAppRepositories } from '../../../../hooks/api/vcs/githubApp/useImportGitHubAppRepositories';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import Button from '../../../generic/Button/Button';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import LabeledCheckbox from '../../../generic/Forms/LabeledCheckbox';
import { icons, IconText } from '../../../generic/Icon';
import Loader from '../../../generic/Loader';
import { teamsUserCanAddProject } from '../../SelectTeam';
import ModalTeamAssignment from '../ModalTeamAssignment';
import QuickImportScanMethodsModal from '../QuickImportScanMethodsModal';
import ServiceImportHeader from '../ServiceImportHeader';
import ServiceImportRepositories from '../ServiceImportRepositories';
import { ServiceMeta } from '../types';
import type { Team } from '../../../../../shared/model-typings/team';
import type { ImportRepository, VcsRepository } from '../../../../../shared/types/routes/vcs/githubApp';
import type { Filter, Repository, ServiceFilter, ServiceQueryParameters } from '../../../../types';
import type { OnBranchChangeFn, OnImportRepositoryFn, OnSelectRepositoryFn } from '../HybridProjectsTable/types';
import type { ScanMethodProjectOverrides, ScanMethodSelections } from '../types';

import styles from '../ServiceImport.module.less';

const SERVICE_DETAILS = ServiceMeta['github-app'];
const PAGE_SIZE = 10;
const TOO_MANY_REQUESTS_STATUS = 429;

// Matches the legacy import flow: pause between keystrokes before searching server-side.
const SEARCH_DEBOUNCE_MS = 750;
const DEFAULT_APP_DISPLAY = VCS_APP_DISPLAY[DEFAULT_VCS_APP];
// The account menu selects a connection, so every account item is keyed on connectionId.
const ACCOUNT_FILTER_KEY = 'connectionId';
// Group accounts under human-readable category headers (matching the legacy org/user grouping).
const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  Organization: 'Organizations',
  User: 'Users',
  Enterprise: 'Enterprises',
};

const asyncNoop = async () => {};

/**
 * `useGitHubAppRepositories` types its error as `Error`, but a failed request actually rejects
 * with the response object itself (see `redux/api`), which carries a numeric `status` instead.
 */
const hasStatus = (error: unknown): error is { status: number } =>
  _.isObject(error) && 'status' in error && _.isNumber(error.status);

/** Only `q`/`page` matter here; the legacy instance/filter fields are supplied separately. */
const buildQuery = (q: string, page: number): ServiceQueryParameters => ({
  q,
  page,
  pageSize: PAGE_SIZE,
  filterType: '',
  filterValue: '',
  instanceName: '',
});

type ImportHeaderProps = {
  isInstalled: boolean;
  isLoading: boolean;
  searchValue: string;
  page: number;
  selectedRepoLength: number;
  /** Connected accounts, grouped into filter categories by type; the menu also hosts "Install". */
  filters: ServiceFilter[];
  selectedFilter?: Filter;
  onSearchChange: (ev: ChangeEvent<HTMLInputElement>) => void;
  onSelectAccount: (filterType: string, connectionId: string) => Promise<void>;
  onInstallGithubApp: () => void;
  onImportSelected: () => Promise<void>;
};

/**
 * The service label/frame reused from the legacy import layout. Once the app is installed the
 * search control and account menu appear; the account menu also offers "Install GitHub App" so
 * the user can connect another account.
 */
const ImportHeader: VoidFunctionComponent<ImportHeaderProps> = ({
  isInstalled,
  isLoading,
  searchValue,
  page,
  selectedRepoLength,
  filters,
  selectedFilter,
  onSearchChange,
  onSelectAccount,
  onInstallGithubApp,
  onImportSelected,
}) => (
  <div className="panel">
    <div className="panel-body padded padded-small">
      <ServiceImportHeader
        serviceDetails={SERVICE_DETAILS}
        filters={filters}
        selectedFilter={selectedFilter}
        query={buildQuery(searchValue, page)}
        instanceNames={[]}
        instanceIsConnected={isInstalled}
        shouldDisplayImportControls={isInstalled}
        selectedRepoLength={selectedRepoLength}
        isLoading={isLoading}
        searchValue={searchValue}
        onSelectInstanceName={asyncNoop}
        onSelectFilter={onSelectAccount}
        onSearchChange={onSearchChange}
        onImport={onImportSelected}
        // No vcs-aware background bulk-import path yet, so "Import All" imports the current
        // selection like the primary button; a true import-all lands in a follow-up ticket.
        onBulkImport={onImportSelected}
        onConnectToService={_.noop}
        onInstallGithubApp={onInstallGithubApp}
      />
    </div>
  </div>
);

type ImportSidebarProps = {
  isInstalled: boolean;
  shouldSubmitBadgePRs: boolean;
  onToggleSubmitBadgePRs: () => void;
};

/** Left column: the auth-note/docs card, plus the badge-PR opt-out once the app is installed. */
const ImportSidebar: VoidFunctionComponent<ImportSidebarProps> = ({
  isInstalled,
  shouldSubmitBadgePRs,
  onToggleSubmitBadgePRs,
}) => (
  <div className="col-xs-3">
    {(SERVICE_DETAILS.authNote || SERVICE_DETAILS.docsURL) && (
      <div className="card card-flat">
        <IconText icon={icons.questionCircle} text={SERVICE_DETAILS.authNote as string} />
        {SERVICE_DETAILS.docsURL && (
          <a
            className="btn btn-default btn-block margin-top"
            href={SERVICE_DETAILS.docsURL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className={styles.docsLink}>
              <IconText icon={icons.book} text={SERVICE_DETAILS.docsLinkText || 'Documentation'} />
            </span>
          </a>
        )}
      </div>
    )}
    {isInstalled && (
      <LabeledCheckbox
        className="margin-top-xl"
        label="Submit badge PRs after import (public GitHub READMEs only)"
        isChecked={shouldSubmitBadgePRs}
        onChange={onToggleSubmitBadgePRs}
        testId="submit-badge-prs-checkbox"
      />
    )}
  </div>
);

type RepositoriesColumnProps = {
  isInstalled: boolean;
  repositories: Repository[];
  selectedRepos: Repository[];
  page: number;
  pageSize: number;
  totalCount?: number;
  hasMore?: boolean;
  isLoading: boolean;
  error?: string | null;
  onSetPage: (page: number) => void;
  onSetPageSize: (pageSize: number) => void;
  onSelectRepository: OnSelectRepositoryFn;
  onInstallGithubApp: () => void;
  onImportRepository: OnImportRepositoryFn;
  onBranchChange: OnBranchChangeFn;
};

/** Right column: the shared repositories table. Not installed → install CTA; installed → repos. */
const RepositoriesColumn: VoidFunctionComponent<RepositoriesColumnProps> = ({
  isInstalled,
  repositories,
  selectedRepos,
  page,
  pageSize,
  totalCount,
  hasMore,
  isLoading,
  error,
  onSetPage,
  onSetPageSize,
  onSelectRepository,
  onInstallGithubApp,
  onImportRepository,
  onBranchChange,
}) => (
  <div className="col-xs-9">
    <ServiceImportRepositories
      repositories={repositories}
      selectedRepos={selectedRepos}
      page={page}
      pageSize={pageSize}
      totalCount={totalCount}
      hasMore={hasMore}
      // vcs-service has a single "installed" state and no separate OAuth
      // "connect the service" step, so isConnected is always satisfied here.
      // hasInstallations is the real gate: false → install CTA, true → repos.
      isConnected
      hasInstallations={isInstalled}
      isLoading={isLoading}
      error={error}
      onSetPage={onSetPage}
      onSetPageSize={onSetPageSize}
      onBranchChange={onBranchChange}
      onRowRedirect={_.noop}
      onSelectRepository={onSelectRepository}
      onImportRepository={onImportRepository}
      onConnectToService={_.noop}
      onInstallGithubApp={onInstallGithubApp}
      githubAppDisplay={DEFAULT_APP_DISPLAY}
    />
  </div>
);

/** Full-panel loader shown while an import is in flight, mirroring the legacy import screen. */
const ImportingLoader: VoidFunctionComponent<{ count: number }> = ({ count }) => (
  <div className={`fossa-service-import ${styles.statusCardColumn}`}>
    <div className="card card-primary">
      <Loader color="#d2b690" size={80} />
      <h2>Importing {count} {plural(count, 'Project')}</h2>
      <p>This may take a few minutes...</p>
    </div>
  </div>
);

/**
 * Terminal state after a batch is handed to the background bulk job, mirroring the legacy bulk
 * import card: the projects appear on the dashboard as the job works through them.
 */
const ImportQueuedCard: VoidFunctionComponent<{ count: number }> = ({ count }) => (
  <div className={`fossa-service-import ${styles.statusCardColumn}`}>
    <div className="card card-primary">
      <h2>Importing {count} {plural(count, 'Project')}</h2>
      <p>This may take several minutes...</p>
      <Button
        className={styles.queuedCardButton}
        icon={icons.arrowLeft}
        onClick={() => browserHistory.push('/projects')}
      >
        Back to projects
      </Button>
    </div>
  </div>
);

type ImportBodyProps = RepositoriesColumnProps & ImportSidebarProps & {
  isInstallationLoading: boolean;
  isInstallationError: boolean;
  isImporting: boolean;
  importCount: number;
  queuedCount: number;
};

/** Body below the header: resolves the installation loading / error / importing / connected states. */
const ImportBody: VoidFunctionComponent<ImportBodyProps> = ({
  isInstallationLoading,
  isInstallationError,
  isInstalled,
  repositories,
  selectedRepos,
  page,
  pageSize,
  totalCount,
  hasMore,
  isLoading,
  error,
  isImporting,
  importCount,
  queuedCount,
  shouldSubmitBadgePRs,
  onToggleSubmitBadgePRs,
  onSetPage,
  onSetPageSize,
  onSelectRepository,
  onInstallGithubApp,
  onImportRepository,
  onBranchChange,
}) => {
  if (isInstallationLoading) {
    return <Loader />;
  }
  if (isInstallationError) {
    return <ErrorAlert status={{ error: 'Failed to load GitHub App status. Please try again.' }} />;
  }
  if (isImporting) {
    return <ImportingLoader count={importCount} />;
  }
  if (queuedCount > 0) {
    return <ImportQueuedCard count={queuedCount} />;
  }
  return (
    <div className="row">
      <ImportSidebar
        isInstalled={isInstalled}
        shouldSubmitBadgePRs={shouldSubmitBadgePRs}
        onToggleSubmitBadgePRs={onToggleSubmitBadgePRs}
      />
      <RepositoriesColumn
        isInstalled={isInstalled}
        repositories={repositories}
        selectedRepos={selectedRepos}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        hasMore={hasMore}
        isLoading={isLoading}
        error={error}
        onSetPage={onSetPage}
        onSetPageSize={onSetPageSize}
        onSelectRepository={onSelectRepository}
        onInstallGithubApp={onInstallGithubApp}
        onImportRepository={onImportRepository}
        onBranchChange={onBranchChange}
      />
    </div>
  );
};

/**
 * Entry container for the vcs-service GitHub App import flow: an org installs the default
 * app here (step one of import, not a settings action), then browses and imports its
 * repositories. Reuses the legacy header + repositories components for layout parity.
 * The repository listing paginates and searches server-side.
 */
export const VcsGitHubAppImport: VoidFunctionComponent = () => {
  const { data: installation, isLoading: isInstallationLoading, isError: isInstallationError } =
    useGitHubAppInstallation(DEFAULT_VCS_APP);

  const isInstalled = Boolean(installation?.installed);

  const startInstall = useStartGitHubAppInstall();
  const onInstallGithubApp = () => startInstall.mutate(DEFAULT_VCS_APP);

  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [selectedRepos, setSelectedRepos] = useState<Repository[]>([]);
  const [branchOverrides, setBranchOverrides] = useState<Record<string, string>>({});
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  // Defaults on, matching the legacy import page's badge-PR checkbox.
  const [shouldSubmitBadgePRs, setShouldSubmitBadgePRs] = useState(true);
  const { debouncedValue: q } = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS);

  const {
    data: installationsPage,
    isLoading: isInstallationsLoading,
    isError: isInstallationsError,
  } = useGitHubAppInstallations(DEFAULT_VCS_APP, { enabled: isInstalled });
  // Until the account list resolves, activeConnectionId is '' and would scope the repo
  // requests to the org's first installation. Gate that flow on the list so it always targets
  // a real, user-visible account.
  const isAccountListLoading = isInstalled && isInstallationsLoading;
  const isAccountListError = isInstalled && isInstallationsError;
  const installations = installationsPage?.results ?? [];
  // Validated against the current list so a stale pick (e.g. the account was just disconnected)
  // falls back to the first connected account automatically.
  const activeConnectionId =
    installations.find((account) => account.connectionId === selectedConnectionId)?.connectionId ??
    installations[0]?.connectionId ??
    '';
  const activeAccount = installations.find((account) => account.connectionId === activeConnectionId);

  // Accounts grouped by type (Organizations / Users / …), matching legacy. The menu also hosts
  // the "Install GitHub App" action, so it renders even with a single connected account.
  const accountsByType = _.groupBy(installations, (account) => account.accountType);
  const filters: ServiceFilter[] = Object.entries(accountsByType).map(([accountType, accounts]) => ({
    category: ACCOUNT_TYPE_LABELS[accountType] ?? accountType,
    key: ACCOUNT_FILTER_KEY,
    filters: accounts.map((account) => ({
      name: account.accountName,
      value: account.connectionId,
      icon: account.avatarUrl ?? '',
    })),
  }));
  const selectedFilter: Filter | undefined = activeAccount
    ? {
        name: activeAccount.accountName,
        value: activeAccount.connectionId,
        icon: activeAccount.avatarUrl ?? '',
      }
    : undefined;

  // A new search or account switch starts over from the first page, matching the legacy import flow.
  useEffect(() => setPage(1), [q, activeConnectionId]);

  // A repo selection (and any branch picks) is scoped to one account's repos, so switching accounts
  // clears both. Otherwise a later import could mix repos across installations, or apply a branch
  // override to a same-named repo in a different account.
  useEffect(() => {
    setSelectedRepos([]);
    setBranchOverrides({});
  }, [activeConnectionId]);

  const {
    data: repositoryPage,
    isFetching: isRepositoriesFetching,
    isError: isRepositoriesError,
    error: repositoriesError,
  } = useGitHubAppRepositories(
    DEFAULT_VCS_APP,
    { page, pageSize, q, connectionId: activeConnectionId || undefined },
    { enabled: isInstalled && !isAccountListLoading && !isAccountListError },
  );

  /*
   * Rate limiting is the failure worth calling out: searching runs against GitHub's search API,
   * whose budget is far smaller than browsing's, so search can fail while browsing works. Any
   * other failure falls back to the shared helper, which keeps the status rather than dropping it.
   */
  let repositoriesErrorMessage: string | null = null;
  if (isRepositoriesError) {
    repositoriesErrorMessage =
      hasStatus(repositoriesError) && repositoriesError.status === TOO_MANY_REQUESTS_STATUS
        ? 'Too many requests to GitHub. Please wait a moment and try again.'
        : String(errorMessageFromResponse(repositoriesError));
  }

  // isFetching (not isLoading) so revisiting a cached page/search/account also shows the
  // loader instead of stale rows while it revalidates, matching the legacy import flow.
  const shouldShowRepositoriesLoader = isRepositoriesFetching;

  const hasMore = repositoryPage?.hasMore ?? false;
  // Clamp page changes so a stale Next (e.g. a click racing a refetch) past the last
  // page is a no-op instead of landing the user on an empty page.
  const onSetPage = (newPage: number) => {
    if (newPage > page && !hasMore) {
      return;
    }
    setPage(Math.max(1, newPage));
  };

  // `VcsRepository` mirrors the legacy `Repository` field names for reuse; the only wire-shape
  // difference (`description: string | null`) is display-only, so the shared table consumes it directly.
  const repositories = (repositoryPage?.results ?? []).map((repo) =>
    branchOverrides[repo.locator] ? { ...repo, branch: branchOverrides[repo.locator] } : repo,
  ) as Repository[];

  // Scope the import to the active account so the clone token and stored connection match the
  // account the listed repos belong to. `activeConnectionId` is always a real id here: import is
  // only reachable once installations have loaded and a repo is selected.
  const importRepositoriesMutation = useImportGitHubAppRepositories(DEFAULT_VCS_APP, activeConnectionId);
  const isImporting = importRepositoriesMutation.isPending;
  // Read from the mutation payload so the loader count stays right after the selection clears.
  const importCount = importRepositoriesMutation.variables?.repos.length ?? 0;
  // A queued response means the batch was handed to the background bulk job; the page switches to
  // a terminal card (matching legacy) since there is no per-build feedback to show.
  const queuedCount = importRepositoriesMutation.data?.queued ?? 0;

  // Import runs through up to two gates before firing, mirroring legacy: a team-assignment modal, then
  // a quick-import scan-methods modal. `pendingRepos`/`pendingTeamIds` carry the choices between steps.
  const { data: teamsData, isLoading: isTeamsLoading } = useTeams(isInstalled);
  const allTeams = teamsData ?? [];
  const organizationId = window.user?.organizationId;
  // The scan-methods modal only renders with an organizationId, so without one the import must not
  // gate on the modal — it would open nothing and the import would never fire.
  const shouldShowScanMethods = useShouldShowQuickImportScanMethods() && Boolean(organizationId);
  const updateScanMethodDefaults = useUpdateOrganizationQuickImportScanMethodsSettings();
  const addToast = useAddToast();
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [pendingRepos, setPendingRepos] = useState<Repository[]>([]);
  const [pendingTeamIds, setPendingTeamIds] = useState<number[]>([]);

  // Repos flow through the table as `Repository` but carry the `VcsRepository` fields the request
  // needs, so narrow here. Branches resolve from the override map rather than the (possibly stale)
  // selection snapshot, so a branch picked after selecting still reaches the request.
  const toImportPayload = (repos: Repository[]): ImportRepository[] =>
    repos.map((repo) => {
      const vcsRepo = repo as unknown as VcsRepository;
      return {
        locator: vcsRepo.locator,
        isPrivate: vcsRepo.isPrivate,
        branch: branchOverrides[vcsRepo.locator] ?? vcsRepo.branch,
        sshCloneURL: vcsRepo.sshCloneURL,
        httpsCloneURL: vcsRepo.httpsCloneURL,
        connectableProjects: vcsRepo.connectableProjects,
      };
    });

  const runImport = (repos: Repository[], teamIds: number[], scan: Partial<ScanMethodProjectOverrides> = {}) => {
    if (repos.length === 0) {
      return;
    }
    importRepositoriesMutation.mutate(
      { repos: toImportPayload(repos), teamIds, submitBadgePRs: shouldSubmitBadgePRs, ...scan },
      {
        onSuccess: () => {
          setSelectedRepos([]);
          setBranchOverrides({});
        },
      },
    );
  };

  const proceedAfterTeams = (repos: Repository[], teamIds: number[]) => {
    setPendingRepos(repos);
    setPendingTeamIds(teamIds);
    if (shouldShowScanMethods) {
      setIsScanModalOpen(true);
      return;
    }
    runImport(repos, teamIds);
  };

  // Entry point for both the header and per-row imports. Teams and scan methods attach only to
  // newly created projects, so a connect-only selection skips both modals.
  const beginImport = (repos: Repository[]) => {
    if (repos.length === 0 || isTeamsLoading || isImporting) {
      return;
    }
    const hasNewImports = repos.some((repo) => !repo.connectableProjects?.length);
    if (!hasNewImports) {
      runImport(repos, []);
      return;
    }
    if (teamsUserCanAddProject(allTeams).length > 0) {
      setPendingRepos(repos);
      setSelectedTeams([]);
      setIsTeamModalOpen(true);
      return;
    }
    proceedAfterTeams(repos, []);
  };

  // "Import All" stays enabled with an empty selection (it imports the selection until the real
  // bulk path lands), so say why nothing happened instead of silently no-oping.
  const onImportSelected = async () => {
    if (selectedRepos.length === 0) {
      addToast({ type: 'info', content: 'Select at least one repository to import.' });
      return;
    }
    beginImport(selectedRepos);
  };
  const onImportRepository = (repo: Repository) => beginImport([repo]);

  const onTeamsSelectionImport = () => {
    setIsTeamModalOpen(false);
    proceedAfterTeams(pendingRepos, selectedTeams.map((team) => team.id));
  };

  const onScanMethodsConfirm = (selections: ScanMethodSelections) => {
    setIsScanModalOpen(false);
    if (selections.quickImportShouldSavePreference && organizationId) {
      updateScanMethodDefaults.mutate({
        organizationId,
        projectDefaultQuickImportVendoredDetectionEnabled: selections.quickImportVendoredDetectionEnabled,
        projectDefaultQuickImportSnippetDetectionEnabled: selections.quickImportSnippetDetectionEnabled,
      });
    }
    runImport(pendingRepos, pendingTeamIds, {
      quickImportVendoredDetectionEnabled: selections.quickImportVendoredDetectionEnabled,
      quickImportSnippetDetectionEnabled: selections.quickImportSnippetDetectionEnabled,
    });
  };

  // Keyed on `locator`: unique by construction, unlike `title`. It is always present on
  // vcs-service rows; the legacy `Repository` type just can't say so.
  const onBranchChange: OnBranchChangeFn = (repo, newBranch) => {
    const { locator } = repo;
    if (!locator) {
      return;
    }
    setBranchOverrides((current) => ({ ...current, [locator]: newBranch }));
  };

  const onSearchChange = (ev: ChangeEvent<HTMLInputElement>) => setSearchValue(ev.target.value);
  // A new page size starts over from the first page, matching the legacy import flow.
  const onSetPageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };
  const onSelectAccount = async (_filterType: string, connectionId: string) =>
    setSelectedConnectionId(connectionId);

  // Keyed on `title` to match how the hybrid table renders its checked state. Selection
  // persists across pages.
  const onSelectRepository = (repo: Repository) =>
    setSelectedRepos((current) =>
      current.some((selected) => selected.title === repo.title)
        ? current.filter((selected) => selected.title !== repo.title)
        : [...current, repo],
    );

  return (
    <div className="fossa-service-import">
      <ImportHeader
        isInstalled={isInstalled}
        isLoading={shouldShowRepositoriesLoader || isImporting || isTeamsLoading}
        searchValue={searchValue}
        page={page}
        selectedRepoLength={selectedRepos.length}
        filters={filters}
        selectedFilter={selectedFilter}
        onSearchChange={onSearchChange}
        onSelectAccount={onSelectAccount}
        onInstallGithubApp={onInstallGithubApp}
        onImportSelected={onImportSelected}
      />
      <ImportBody
        isInstallationLoading={isInstallationLoading || isAccountListLoading}
        isInstallationError={isInstallationError || isAccountListError}
        isInstalled={isInstalled}
        repositories={repositories}
        selectedRepos={selectedRepos}
        page={page}
        pageSize={pageSize}
        totalCount={repositoryPage?.total}
        hasMore={hasMore}
        isLoading={shouldShowRepositoriesLoader}
        error={repositoriesErrorMessage}
        isImporting={isImporting}
        importCount={importCount}
        queuedCount={queuedCount}
        shouldSubmitBadgePRs={shouldSubmitBadgePRs}
        onToggleSubmitBadgePRs={() => setShouldSubmitBadgePRs((current) => !current)}
        onSetPage={onSetPage}
        onSetPageSize={onSetPageSize}
        onSelectRepository={onSelectRepository}
        onInstallGithubApp={onInstallGithubApp}
        onImportRepository={onImportRepository}
        onBranchChange={onBranchChange}
      />
      <ModalTeamAssignment
        show={isTeamModalOpen}
        onHide={() => setIsTeamModalOpen(false)}
        allTeams={allTeams}
        selectedTeams={selectedTeams}
        onTeamsSelected={setSelectedTeams}
        onTeamsSelectionImport={onTeamsSelectionImport}
        hasNextStep={shouldShowScanMethods}
      />
      {organizationId && (
        <QuickImportScanMethodsModal
          organizationId={organizationId}
          show={isScanModalOpen}
          onBack={() => setIsScanModalOpen(false)}
          onConfirm={onScanMethodsConfirm}
        />
      )}
    </div>
  );
};
